import { ClinicalTrialListPageView } from './ClinicalTrialList.view';
import { useCallback, useEffect, useState } from 'react';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { usePaginationAndSort } from '@restore/helpers';
import { useInventorySlice } from '@restore/inventory/inventory.slice';
import { useClinicalTrialsListSlice } from '@restore/clinicalTrialsList/clinicalTrialsList.slice';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { NestedFilter } from '@components/NestedFilter/NestedFilter';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { useClinicalTrialFilters } from '@restore/clinicalTrialsList/useClinicalTrialsFilters';
import useDebounceFn from '@shared/hooks/useDebouncedValue';
import { useAppSelector } from '@hooks/redux';
import { UserRoles } from '@dynamika-shared/types/auth';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { AllFilterItem } from '@components/NestedFilter/types';

type OnSortColumnType = (name: string, dir?: string) => void;

export const ClinicalTrialListPage = () => {
  const userRole = useAppSelector(state => state?.auth?.data?.selectedRole);

  const isValidUser = [
    UserRoles.PROJECT_MANAGER,
    UserRoles.ADMIN,
    UserRoles.SUPPORT,
    UserRoles.VALIDATION_SPECIALIST,
  ].includes(userRole as UserRoles);

  const { sortBy, currentPage, currentPageSize, setSortBy, setCurrentPage } =
    usePaginationAndSort('clinicalTrials');

  const sort = sortBy ?? undefined;

  const {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilters,
    toggleSelectedFilters,
    selectedFilters,
    filterOnClick,
    search,
    setSearch = () => null,
    args,
  } = useClinicalTrialFilters();

  useBreadcrumbs([['Clinical Trials', 'clinicalTrial']]);

  const [data, loading, fetch] = useClinicalTrialsListSlice(
    (state, actions) => [
      state.data,
      state.loading,
      actions.fetchClinicalTrialsList,
    ]
  );

  const fetchInventory = useInventorySlice(
    (_, actions) => actions.fetchInventory
  );

  useEffect(() => {
    fetchInventory(undefined);
  }, []);

  useEffect(() => {
    fetch({
      sort,
      size: currentPageSize,
      page: currentPage,
      ...(search ? { search } : {}),
      ...args,
    });
  }, [sort, currentPageSize, currentPage, search, args.sponsorId, args.status]);

  const onSort: OnSortColumnType = (name, sortState) => {
    const newSort = sortState ? `${name},${sortState}` : undefined;
    setSortBy({ listName: 'clinicalTrials', newSortBy: newSort });
    fetch({
      sort: newSort,
      page: currentPage,
      size: currentPageSize,
      ...(search && { search }),
      ...args,
    });
  };

  const pagination = useMakePagination(
    'clinicalTrials',
    !!data?.content?.length
  );

  const [searchValue, setSearchValue] = useState<string>();

  const { run: delayedSearch } = useDebounceFn(nextValue => {
    setSearch(nextValue);
    setCurrentPage({ listName: 'clinicalTrials', newPage: 1 });
  }, 500);

  const statusSortOption = useCallback(
    <Val extends string>(a: AllFilterItem<Val>, b: AllFilterItem<Val>) => {
      const order: StudyStatusEnum[] = [
        StudyStatusEnum.startUp,
        StudyStatusEnum.draft,
        StudyStatusEnum.maintenance,
        StudyStatusEnum.closeUp,
        StudyStatusEnum.completed,
        StudyStatusEnum.onHold,
        StudyStatusEnum.cancelled,
      ];
      return (
        order.indexOf(a.value as StudyStatusEnum) -
        order.indexOf(b.value as StudyStatusEnum)
      );
    },
    []
  );

  return (
    <div data-testid='ClinicalTrialList' className='h-full'>
      <ClinicalTrialListPageView
        onSort={onSort}
        sortState={sortBy}
        pagination={pagination}
        loading={loading}
        clinicalTrialsData={data?.content || []}
        totalItems={data?.totalElements ?? 0}
      >
        {isValidUser && (
          <div className='mt-[1.1rem] flex items-start justify-end [&>.header]:mt-0 gap-2'>
            <div className='mt-0.5'>
              <NestedFilter
                onClick={filterOnClick}
                items={filters}
                subFilters={subFilters}
                allFilters={allFilters}
                sortOption={statusSortOption}
                closeTag={toggleActiveFilters}
                toggleSelectedFilter={(name, value) =>
                  toggleSelectedFilters({ name, value })
                }
                selectedFilters={selectedFilters}
                overlayClassName={'w-[22.4rem]'}
              />
            </div>
            <Input
              value={searchValue}
              onChange={e => {
                setSearchValue(e.target.value);
                delayedSearch(e.target.value);
              }}
              className='!mt-0 h-[4.8rem] min-w-[40rem] !rounded-[3.2rem] px-2'
              placeholder='Search by Clinical Trial Name'
              suffix={
                <SearchSuffix
                  disabled={!searchValue}
                  action={() => {
                    setSearchValue('');
                    setSearch('');
                    setCurrentPage({ listName: 'clinicalTrials', newPage: 1 });
                  }}
                />
              }
            />
          </div>
        )}
      </ClinicalTrialListPageView>
    </div>
  );
};
